// ModelPane — the 3D half of Find in Sheets & Model, as a model.
//
// The pane used to be a list of anchors and nothing to look at, so a
// search could not show WHERE its answer sits and nobody could tell
// whether the model being searched was the one just pushed. This streams
// the project's live model (the same manifest the model viewer opens)
// and makes the search visible in it:
//
//   - every element behind the 3D hits is lit in orange; with "Only the
//     results" on, everything else is hidden
//   - the hit picked in the list is blue and the camera frames it
//   - clicking an element in the model asks the search about its type,
//     so the sheets side answers "where is this drawn"
//
// Hits are element TYPES on a level (utils/modelIngest); POST
// /anchors/uids expands them to their elements, once per result set.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Switch, Tooltip } from "antd";
import { AimOutlined } from "@ant-design/icons";
import WebGlApp from "webgl";
import { url } from "api";
import { expandAnchorUids } from "api/anchors/anchorApi";
import { boxForUids } from "pages/Tracking/stageHelpers";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import { attachBuildingGrid } from "helpers/buildingGridLoad";

const HIT_COLOR = "#fe6919";
const PICK_COLOR = "#2F6FED";
// Past this many lit elements the highlight still applies but pinning
// every one of them against the pixel floor would load the whole model.
const PIN_LIMIT = 20000;

export default function ModelPane({ projectId, hits = [], selected = null, onPickElement, modelInfo = null }) {
  const containerRef = useRef(null);
  const WebGL = useRef(null);
  const pollRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noModel, setNoModel] = useState(false);
  const [onlyResults, setOnlyResults] = useState(true);
  const [expansion, setExpansion] = useState({ uids: [], byAnchor: {}, capped: false });
  const [expanding, setExpanding] = useState(false);
  const paintedRef = useRef(new Set());
  const pickRef = useRef(onPickElement);
  pickRef.current = onPickElement;

  const viewer = () => WebGL.current && WebGL.current.viewer;
  const store = () => { const v = viewer(); return v && v.streaming && v.streaming.state; };

  // ---- the viewer, once per project ----
  useEffect(() => {
    if (!projectId || !containerRef.current) return undefined;
    const noop = () => {};
    setReady(false); setNoModel(false); setLoading(true);
    try {
      WebGL.current = new WebGlApp(
        containerRef.current,
        { glbs: [], manifestUrl: `${url}/uploads/${projectId}/manifest.json.gz` },
        {
          setClickedChild: noop, setLoading, setError: noop, setPercentsLoaded: noop,
          setAllowToResize: noop, setModal: noop, setCoordsForPopover: noop,
          findInJsonUsingViewer: (uid) => { if (uid && pickRef.current) pickRef.current(uid); },
          setIsPopOver: noop, onHoverMesh: noop, selectedMesh: { current: null }, project: { _id: projectId },
        },
        null, false, false
      );
      attachBuildingGrid(WebGL.current.viewer, projectId);
    } catch (e) {
      setNoModel(true);
      return undefined;
    }
    // The streaming session exists once the manifest has landed. Waited
    // for as long as it takes - a big model can take minutes - and a
    // missing model is only declared after a long quiet spell, and
    // withdrawn the moment it turns up after all.
    let tries = 0;
    pollRef.current = setInterval(() => {
      tries += 1;
      const v = viewer();
      if (v && v.streaming && v.streaming.state) {
        setReady(true); setNoModel(false);
        clearInterval(pollRef.current); pollRef.current = null;
        return;
      }
      if (tries === 120) setNoModel(true);          // 60 s of nothing
      if (tries > 1200) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 500);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { if (WebGL.current && typeof WebGL.current.dispose === "function") WebGL.current.dispose(); } catch (e) { /* best effort */ }
      WebGL.current = null;
      paintedRef.current = new Set();
    };
  }, [projectId]);

  // The canvas follows its pane (split drag, expand, window resize).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const v = viewer();
      try { if (v && typeof v.onWindowResize === "function") v.onWindowResize(); } catch (e) { /* best effort */ }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- expand the hits to their elements, once per result set ----
  const hitIds = useMemo(() => hits.map((h) => String(h._id)).slice(0, 500), [hits]);
  const hitKey = hitIds.join(",");
  useEffect(() => {
    let alive = true;
    if (!projectId || !hitIds.length) { setExpansion({ uids: [], byAnchor: {}, capped: false }); return undefined; }
    setExpanding(true);
    expandAnchorUids(projectId, hitIds)
      .then((r) => { if (alive) setExpansion({ uids: r.uids || [], byAnchor: r.byAnchor || {}, capped: !!r.capped }); })
      .catch(() => { if (alive) setExpansion({ uids: [], byAnchor: {}, capped: false }); })
      .finally(() => { if (alive) setExpanding(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, hitKey]);

  const selectedUids = useMemo(() => {
    if (!selected || selected.world !== "3d") return [];
    const range = expansion.byAnchor[String(selected._id)];
    if (range) return expansion.uids.slice(range[0], range[1]);
    return selected.uniqueId ? [selected.uniqueId] : [];
  }, [selected, expansion]);

  // ---- paint: results orange, the picked one blue, the rest hidden or left ----
  useEffect(() => {
    const st = store();
    if (!ready || !st) return;
    const v = viewer();
    const want = new Map();
    for (const u of expansion.uids) want.set(u, HIT_COLOR);
    for (const u of selectedUids) want.set(u, PICK_COLOR);
    const gone = [];
    paintedRef.current.forEach((u) => { if (!want.has(u)) gone.push(u); });
    try {
      if (gone.length && typeof st.bulkClearHighlights === "function") st.bulkClearHighlights(gone);
      if (want.size) st.bulkHighlight([...want.entries()]);
      if (onlyResults && expansion.uids.length) st.setIsolation(expansion.uids);
      else if (typeof st.clearIsolation === "function") st.clearIsolation();
      else st.setIsolation(null);
      const streaming = v && v.streaming;
      if (streaming && typeof streaming.setPinnedUids === "function") {
        const pin = selectedUids.length ? selectedUids : expansion.uids;
        streaming.setPinnedUids(pin.length <= PIN_LIMIT ? pin : pin.slice(0, PIN_LIMIT));
      }
    } catch (e) { /* the model still draws */ }
    paintedRef.current = new Set(want.keys());
  }, [ready, expansion, selectedUids, onlyResults]);

  // ---- frame: the picked hit, else the whole result ----
  const frame = useCallback((uids) => {
    const v = viewer();
    const c = v && v.streaming && v.streaming.controller;
    const adapter = c && c.adapter;
    if (!v || !v.cameraControls || !adapter || !adapter.activeUidOwner || !uids.length) return false;
    const box = boxForUids((uid) => {
      const o = adapter.activeUidOwner.get(uid);
      return o && o.record ? { mesh: o.record.mesh, index: o.record.index } : null;
    }, uids);
    if (!box) return false;
    try { v.cameraControls.fitToBox(box, true, { paddingTop: 0.2, paddingBottom: 0.2, paddingLeft: 0.2, paddingRight: 0.2 }); } catch (e) { return false; }
    return true;
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const target = selectedUids.length ? selectedUids : expansion.uids;
    if (!target.length) return undefined;
    // Tiles for the target may still be arriving; try again a few times.
    let n = 0;
    const t = setInterval(() => { n += 1; if (frame(target) || n > 8) clearInterval(t); }, 900);
    frame(target);
    return () => clearInterval(t);
  }, [ready, selectedUids, expansion, frame]);

  const lit = expansion.uids.length;
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#f5f6f7" }}>
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <div style={{
        position: "absolute", left: 8, top: 8, right: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        background: "#ffffffe6", border: "1px solid #eee", borderRadius: 6, padding: "4px 8px", fontSize: 12,
      }}>
        <span style={{ color: "#555" }}>
          {modelInfo && modelInfo.exportVersion ? `Live model ${modelInfo.exportVersion}` : "Live model"}
        </span>
        <Tooltip title="Hide everything that is not a result">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Switch size="small" checked={onlyResults} onChange={setOnlyResults} /> Only the results
          </span>
        </Tooltip>
        <span style={{ color: lit ? "#fe6919" : "#999" }}>
          {expanding ? "finding the elements…" : hits.length ? `${lit.toLocaleString()} element${lit === 1 ? "" : "s"} lit${expansion.capped ? " (capped)" : ""}` : "search to light elements"}
        </span>
        <Tooltip title={selectedUids.length ? "Frame the picked result" : "Frame the results"}>
          <Button size="small" icon={<AimOutlined />} disabled={!ready || (!lit && !selectedUids.length)}
            onClick={() => frame(selectedUids.length ? selectedUids : expansion.uids)} style={{ marginLeft: "auto" }} />
        </Tooltip>
        <ViewerViewButtons viewer={() => WebGL.current && WebGL.current.viewer} variant="dark" />
        <ViewerEffectsButton viewer={() => WebGL.current && WebGL.current.viewer} variant="dark" style={{ background: "#f0f0f0", color: "#262626" }} />
      </div>
      {modelInfo && modelInfo.stale && (
        <div style={{ position: "absolute", left: 8, bottom: 8, right: 8, background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: "#874d00" }}>
          A newer model push is live than the one the search was indexed from. The index is being rebuilt from it now; search again in a few minutes for results from the latest push.
        </div>
      )}
      {!ready && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
          <div style={{ fontSize: 12.5, color: "#777", background: "#ffffffcc", padding: "6px 10px", borderRadius: 6, textAlign: "center", maxWidth: 320 }}>
            {noModel
              ? "Still waiting for the model. A large model can take a few minutes; if this project has never been published from the Revit plugin there is nothing to show."
              : (loading ? "Loading the live model…" : "Starting the model…")}
          </div>
        </div>
      )}
      {/* THE SHARED TOOLBAR, and it is deliberately the LAST child.
          It positions itself absolutely against this container, so
          inside the header flex above it escaped that row and landed
          under the "Loading the live model" overlay - which paints
          after it in DOM order (Mark, headless on staging main.378ba59d:
          all four buttons reported covered). Last child, so nothing
          paints over it.
          gear={false} because this pane already renders its own
          ViewerEffectsButton in the header - two gears is the same
          duplication this change exists to remove. */}
{/* A CORNER OF ITS OWN. `viewer-zoom` only carries coordinates in
          the model-view and timeline stylesheets, so on this page the
          class is inert and the toolbar would sit wherever it fell in
          flow (Mark measured x=-41 on tracking, lying across twenty
          controls). A positioned wrapper gives it the same bottom-left
          corner the model view has. */}
      <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 5,
                   display: "flex", justifyContent: "flex-end", pointerEvents: "none" }}>
        <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer} gear={false} />
      </div>
    </div>
  );
}
